/**
 * M27 task-07 — edit an LLM model (ADM-01/02; HUB-082). Loads the row via
 * GET /llm-models/:id, prefilling everything EXCEPT the API key, which is
 * write-only: the field starts blank and is sent only when the operator types
 * a replacement (leaving it blank keeps the stored key). The presence
 * indicator comes from the response `hasApiKey`. Delete is here too — a model
 * still bound to a purpose returns 409 `model_assigned`, surfaced inline.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import {
  requiresBaseUrl,
  type LlmModel,
  type LlmModelUpdate,
} from '@verybigthings/trackframe-shared/schemas';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SecretField } from '@/components/SecretField';
import { BASE_URL_HINTS, PROVIDERS, ProviderSelect, type Provider } from './provider-select';
import { hub } from '@/lib/hub-client';

function isProvider(value: string): value is Provider {
  return (PROVIDERS as readonly string[]).includes(value);
}

export function ModelsEdit() {
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [model, setModel] = useState<LlmModel | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [internalName, setInternalName] = useState('');
  const [provider, setProvider] = useState<Provider>('openai');
  const [modelName, setModelName] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    hub
      .llmModelGet({ params: { id } })
      .then((row: LlmModel) => {
        setModel(row);
        setInternalName(row.internalName);
        setProvider(isProvider(row.provider) ? row.provider : 'openai');
        setModelName(row.modelName);
        setBaseUrl(row.baseUrl ?? '');
        setIsDefault(row.isDefault);
      })
      .catch((err) => setLoadError((err as Error).message));
  }, [id]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body: LlmModelUpdate = {
        internalName: internalName.trim(),
        provider,
        modelName: modelName.trim(),
        baseUrl: requiresBaseUrl(provider) ? (baseUrl.trim() || null) : null,
        ...(apiKey ? { apiKey } : {}),
        ...(isDefault ? { isDefault: true } : {}),
      };
      const row: LlmModel = await hub.llmModelUpdate({ params: { id }, body });
      setModel(row);
      setApiKey('');
      navigate('/admin/models');
    } catch (err) {
      setError(err instanceof HubClientError ? err.message : (err as Error).message);
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    setDeleteBusy(true);
    setError(null);
    try {
      await hub.llmModelDelete({ params: { id } });
      navigate('/admin/models');
    } catch (err) {
      if (err instanceof HubClientError && err.code === 'model_assigned') {
        setError('This model is still assigned to at least one purpose. Reassign those purposes first.');
      } else {
        setError((err as Error).message);
      }
      setDeleteOpen(false);
      setDeleteBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight">Edit model</h1>
        <Alert variant="destructive">{loadError}</Alert>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Edit model</h1>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Model</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="internalName">Name</Label>
              <Input
                id="internalName"
                value={internalName}
                onChange={(e) => setInternalName(e.target.value)}
                required
              />
            </div>
            <ProviderSelect value={provider} onChange={setProvider} />
            <div className="space-y-2">
              <Label htmlFor="modelName">Model name</Label>
              <Input id="modelName" value={modelName} onChange={(e) => setModelName(e.target.value)} required />
            </div>
            {requiresBaseUrl(provider) && (
              <div className="space-y-2">
                <Label htmlFor="baseUrl">{BASE_URL_HINTS[provider]?.label ?? 'Base URL'}</Label>
                <Input
                  id="baseUrl"
                  placeholder={BASE_URL_HINTS[provider]?.placeholder}
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">{BASE_URL_HINTS[provider]?.hint}</p>
              </div>
            )}
            <SecretField
              id="apiKey"
              label="API key"
              hasSecret={model?.hasApiKey ?? false}
              value={apiKey}
              onChange={setApiKey}
            />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
              Hub default model
            </label>
            {error && <Alert variant="destructive">{error}</Alert>}
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <Button type="submit" disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate('/admin/models')}>
                  Cancel
                </Button>
              </div>
              <Button type="button" variant="destructive" onClick={() => setDeleteOpen(true)}>
                Delete
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      <ConfirmDialog
        open={deleteOpen}
        title="Delete model?"
        description={model ? `"${model.internalName}" will be removed.` : undefined}
        confirmLabel="Delete"
        destructive
        busy={deleteBusy}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}
